import type { SalesContractFilters, SalesContractFormData } from "@/features/dashboard/types/sales-contract";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { salesContractService } from "../services/sales-contract-service";

const QUERY_KEY = "sales-contracts";

export const useSalesContractsQuery = (filters?: SalesContractFilters, options?: { enabled?: boolean }) => {
	return useQuery({
		queryKey: [QUERY_KEY, filters],
		queryFn: () => salesContractService.list(filters),
		...options,
	});
};

export const useSalesContractQuery = (id: string) => {
	return useQuery({
		queryKey: [QUERY_KEY, id],
		queryFn: () => salesContractService.findById(id),
		enabled: !!id,
	});
};

export const useSalesContractsByProjectQuery = (projectId: string) => {
	return useQuery({
		queryKey: [QUERY_KEY, "by-project", projectId],
		queryFn: () => salesContractService.list({ project_id: projectId }),
		enabled: !!projectId,
	});
};

export const useSalesContractDashboardQuery = (dateFrom?: string, dateTo?: string, status?: string, salespersonId?: number) => {
	return useQuery({
		queryKey: [QUERY_KEY, "dashboard", dateFrom, dateTo, status, salespersonId],
		queryFn: () => salesContractService.getDashboard(dateFrom, dateTo, status, salespersonId),
		refetchInterval: 60000, // Refetch every minute
	});
};

export const useSalesContractCalendarQuery = (start?: string, end?: string) => {
	return useQuery({
		queryKey: [QUERY_KEY, "calendar", start, end],
		queryFn: () => salesContractService.getCalendarEvents(start, end),
	});
};

export const useGroupedSalesContractsQuery = (params: Record<string, unknown> = {}) => {
	return useQuery({
		queryKey: [QUERY_KEY, "grouped", params],
		queryFn: () => salesContractService.grouped(params),
		enabled: !!params.group_by,
	});
};

export const useCreateSalesContractMutation = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (data: SalesContractFormData) => salesContractService.create(data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
			toast.success("Sales contract created successfully");
		},
		onError: (error: { response?: { data?: { message?: string } } }) => {
			toast.error(error.response?.data?.message || "Failed to create sales contract");
		},
	});
};

export const useUpdateSalesContractMutation = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ id, data }: { id: string; data: Partial<SalesContractFormData> }) => salesContractService.update(id, data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
			toast.success("Sales contract updated successfully");
		},
		onError: (error: { response?: { data?: { message?: string } } }) => {
			toast.error(error.response?.data?.message || "Failed to update sales contract");
		},
	});
};

export const useUpdateContractAsComplete = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ id }: { id: string }) => salesContractService.markAsComplete(id),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
			toast.success("Sales contract mark as completed");
		},
		onError: (error: { response?: { data?: { message?: string } } }) => {
			toast.error(error.response?.data?.message || "Failed to update sales contract status");
		},
	});
};

export const useDeleteSalesContractMutation = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (id: string) => salesContractService.delete(id),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
			toast.success("Sales contract deleted successfully");
		},
		onError: (error: { response?: { data?: { message?: string } } }) => {
			toast.error(error.response?.data?.message || "Failed to delete sales contract");
		},
	});
};

export const useExtendSalesContractMutation = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ id, extendedEndDate }: { id: string; extendedEndDate: string }) => salesContractService.extend(id, extendedEndDate),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
			toast.success("Sales contract extended successfully");
		},
		onError: (error: { response?: { data?: { message?: string } } }) => {
			toast.error(error.response?.data?.message || "Failed to extend sales contract");
		},
	});
};

export const useUpdateCollectionStatusMutation = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (data: {
			id: string;
			collection_status: "pending" | "collected";
			collection_remarks?: string;
			date_collected?: string;
			collected_by?: string;
		}) => salesContractService.updateCollectionStatus(data.id, data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
			toast.success("Collection status updated successfully");
		},
		onError: (error: { response?: { data?: { message?: string } } }) => {
			toast.error(error.response?.data?.message || "Failed to update collection status");
		},
	});
};

export const useCollectionReportQuery = (year: number, salespersonId?: number) => {
	return useQuery({
		queryKey: [QUERY_KEY, "collection-report", year, salespersonId],
		queryFn: () => salesContractService.getCollectionReport(year, salespersonId),
		refetchInterval: 300000, // Refetch every 5 minutes
	});
};
